import { Table, Button, Space, Tag, Popconfirm, Empty, Spin } from 'antd';
import { Edit2, Trash2, Plus } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';
import {
  FluxoCaixaLancamento,
  FluxoCaixaTipo,
  FLUXO_CAIXA_CATEGORIA_LABELS,
  FLUXO_CAIXA_CENTRO_CUSTO_LABELS,
  FLUXO_CAIXA_TIPO_LABELS,
} from '../types/fluxoCaixa';

interface FluxoCaixaTableProps {
  lancamentos: FluxoCaixaLancamento[];
  loading: boolean;
  onEdit: (lancamento: FluxoCaixaLancamento) => void;
  onDelete: (lancamento: FluxoCaixaLancamento) => void;
  onNovoLancamento?: () => void;
}

export function FluxoCaixaTable({
  lancamentos,
  loading,
  onEdit,
  onDelete,
  onNovoLancamento,
}: FluxoCaixaTableProps) {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spin />
      </div>
    );
  }

  if (lancamentos.length === 0) {
    return (
      <Empty description="Nenhum lançamento neste período">
        {onNovoLancamento && (
          <Button type="primary" icon={<Plus className="w-4 h-4" />} onClick={onNovoLancamento}>
            Novo Lançamento
          </Button>
        )}
      </Empty>
    );
  }

  const columns = [
    {
      title: 'Data',
      dataIndex: 'data_lancamento',
      key: 'data_lancamento',
      sorter: (a: FluxoCaixaLancamento, b: FluxoCaixaLancamento) =>
        a.data_lancamento.localeCompare(b.data_lancamento),
      defaultSortOrder: 'descend' as const,
      render: (data: string) => formatDate(data),
      width: 110,
    },
    {
      title: 'Tipo',
      dataIndex: 'lancamento_tipo',
      key: 'lancamento_tipo',
      sorter: (a: FluxoCaixaLancamento, b: FluxoCaixaLancamento) =>
        a.lancamento_tipo.localeCompare(b.lancamento_tipo),
      render: (tipo: FluxoCaixaTipo) => (
        <Tag color={tipo === FluxoCaixaTipo.ENTRADA ? 'green' : 'red'}>
          {tipo === FluxoCaixaTipo.ENTRADA ? '🟢' : '🔴'} {FLUXO_CAIXA_TIPO_LABELS[tipo]}
        </Tag>
      ),
      width: 130,
    },
    {
      title: 'Categoria',
      dataIndex: 'categoria',
      key: 'categoria',
      sorter: (a: FluxoCaixaLancamento, b: FluxoCaixaLancamento) =>
        a.categoria.localeCompare(b.categoria),
      render: (cat: FluxoCaixaLancamento['categoria']) => FLUXO_CAIXA_CATEGORIA_LABELS[cat],
      width: 160,
    },
    {
      title: 'Centro de Custo',
      dataIndex: 'centro_custo',
      key: 'centro_custo',
      render: (cc: FluxoCaixaLancamento['centro_custo']) =>
        cc ? FLUXO_CAIXA_CENTRO_CUSTO_LABELS[cc] : '—',
      width: 140,
    },
    {
      title: 'Descrição',
      dataIndex: 'descricao',
      key: 'descricao',
      ellipsis: true,
      render: (val?: string | null) => val || '—',
    },
    {
      title: 'Valor',
      dataIndex: 'valor',
      key: 'valor',
      sorter: (a: FluxoCaixaLancamento, b: FluxoCaixaLancamento) => a.valor - b.valor,
      render: (valor: number, record: FluxoCaixaLancamento) => (
        <span className={record.lancamento_tipo === FluxoCaixaTipo.ENTRADA ? 'text-green-600' : 'text-red-600'}>
          {formatCurrency(valor)}
        </span>
      ),
      width: 140,
      align: 'right' as const,
    },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_: unknown, record: FluxoCaixaLancamento) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<Edit2 className="w-4 h-4" />}
            onClick={() => onEdit(record)}
            title="Editar"
          />
          <Popconfirm
            title="Deseja deletar este lançamento?"
            description={
              <span>
                {formatDate(record.data_lancamento)} — {FLUXO_CAIXA_TIPO_LABELS[record.lancamento_tipo]} — {formatCurrency(record.valor)}
              </span>
            }
            onConfirm={() => onDelete(record)}
            okText="Confirmar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<Trash2 className="w-4 h-4" />}
              title="Deletar"
            />
          </Popconfirm>
        </Space>
      ),
      width: 100,
      fixed: 'right' as const,
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={lancamentos}
      rowKey="id"
      pagination={{
        pageSize: 20,
        showTotal: total => `${total} lançamento${total !== 1 ? 's' : ''}`,
        showSizeChanger: false,
      }}
      size="small"
      scroll={{ x: 900 }}
    />
  );
}
